import React from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import $image_url from 'js/utils/image_url';
import { currentUser } from 'js/utils/user';
import { uploadUrl } from './uploadUrl';
import PremiumLock from 'js/premium/PremiumLock';
import trackClick from 'js/utils/trackClick';

const Header = styled.h2`
  display: block;
  margin: 24px 12px 12px;
  padding: 12px 0;
  border-bottom: 1px solid #dddfe2;
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 500;
`;

const HighlightList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Description = styled.p`
  margin: 0px 12px;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  color: #888888;
`;

const ButtonishAnchor = styled.a`
  color: #000;

  &:hover,
  &:active,
  &:visited {
    color: #000;
    text-decoration: none;

    > *,
    > * > * {
      text-decoration: underline;
    }

    > p {
      text-decoration: none;
    }
  }
`;

const ProminentLinkIcon = styled.img`
  margin-right: 12px;
  height: 20px;
  width: 20px;
`;

const ProminentLinkIconWrapper = styled.span`
  margin: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
`;

const NotificationDot = styled.div`
  position: absolute;
  top: 0;
  right: -8px;
  margin-left: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #bb0202;
`;

const ProminentLink = styled(ButtonishAnchor)`
  display: flex;
  align-items: center;
  margin: 12px 8px;
  padding: 8px 8px 8px 0;
  border-radius: 2px;
  color: #000;
  font-size: 16px;
  font-weight: 400;

  &:hover,
  &:active,
  &:visited {
    color: #000;
    cursor: pointer;

    > ${ProminentLinkIconWrapper}, > ${ProminentLinkIconWrapper} * {
      text-decoration: none;
    }
  }

  > img {
    margin: 8px;
  }

  > div {
    flex: 1 0 auto;

    > div {
      display: inline-block;
      position: relative;
    }
  }
`;

const DescriptionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Subheader = styled.h3`
  margin: 22px 12px 4px;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0em;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export type Props = {
  startSearch: () => void;
  hasUnseenClaimablePapers?: boolean;
  hasUnseenUploadableStubs?: boolean;
};

const ToolsMenu = ({
  startSearch,
  hasUnseenClaimablePapers = false,
  hasUnseenUploadableStubs = false,
}: Props) => {
  const handleOnUploadClick = () => {
    trackClick({
      source: 'nav-bar-tools--upload-button',
    });
  };

  return (
    <>
      <Header>Explore Academia products</Header>
      <HighlightList>
        <li>
          <ProminentLink
            href={uploadUrl({
              hasUnseenUploadableStubs,
              hasUnseenClaimablePapers,
              trigger: 'tools',
            })}
            onClick={handleOnUploadClick}
          >
            <ProminentLinkIcon
              src={$image_url('header/focused/up_arrow.svg')}
            />
            <div>
              <div>
                Upload
                {(hasUnseenClaimablePapers || hasUnseenUploadableStubs) && (
                  <NotificationDot />
                )}
              </div>
            </div>
          </ProminentLink>
        </li>

        <li>
          <ProminentLink
            href={routes.academia_journals_url({
              from_navbar: true,
              trigger: 'tools',
              source: 'tools',
            })}
          >
            <ProminentLinkIcon
              src={$image_url('header/focused/open_book.svg')}
            />
            <div>
              <div>Academia.edu Journals</div>
            </div>
          </ProminentLink>
        </li>

        <li>
          <ProminentLink
            {...(currentUser?.premium
              ? { onClick: startSearch, role: 'button' }
              : {
                  href: routes.upgrade_url({
                    feature: 'search',
                    from_navbar: true,
                    trigger: 'tools',
                  }),
                })}
          >
            <ProminentLinkIcon
              src={$image_url('header/focused/magnifying_glass.svg')}
            />
            <div>Advanced Search</div>
            <PremiumLock />
          </ProminentLink>
        </li>
        <li>
          <ProminentLink
            href={routes.research_index_url({
              from_navbar: true,
              trigger: 'tools',
            })}
          >
            <ProminentLinkIcon
              src={$image_url('header/focused/packed_folder_uninverted.svg')}
            />
            <div>PDF Packs</div>
            <PremiumLock />
          </ProminentLink>
        </li>
        <li>
          <ProminentLink
            href={
              currentUser?.premium
                ? routes.saved_searches_url({
                    from_navbar: true,
                    trigger: 'tools',
                  })
                : routes.upgrade_url({
                    feature: 'SAVED_SEARCH',
                    from_navbar: true,
                    trigger: 'tools',
                  })
            }
          >
            <ProminentLinkIcon
              src={$image_url('header/focused/alert_bell.svg')}
            />
            <div>Search Alerts</div>
            <PremiumLock />
          </ProminentLink>
        </li>
        <li>
          <ProminentLink
            href={routes.courses_url({ from_navbar: true, trigger: 'tools' })}
          >
            <ProminentLinkIcon src={$image_url('header/focused/courses.svg')} />
            <div>Courses</div>
          </ProminentLink>
        </li>
      </HighlightList>
      <Header>Accelerate your research</Header>
      <DescriptionList>
        <li>
          <ButtonishAnchor
            href={routes.recommendations_url({
              from_navbar: true,
              trigger: 'tools',
            })}
          >
            <Subheader>Improve your recommendations</Subheader>
            <Description>Improve on-site recommendations</Description>
          </ButtonishAnchor>
        </li>
        <li>
          <ButtonishAnchor
            href={routes.bookmarks_url({
              from_navbar: true,
              trigger: 'tools',
            })}
          >
            <Subheader>Bookmarks</Subheader>
            <Description>Your recent reads and saved papers</Description>
          </ButtonishAnchor>
        </li>
        <li>
          <ButtonishAnchor
            href={
              currentUser?.premium
                ? routes.bookmarks_url({
                    from_navbar: true,
                    trigger: 'tools',
                  })
                : routes.upgrade_url({
                    feature: 'translations',
                    from_navbar: true,
                    trigger: 'tools',
                  })
            }
          >
            <Subheader>
              <div>Translation</div>
              <PremiumLock />
            </Subheader>
            <Description>Discover and translate research</Description>
          </ButtonishAnchor>
        </li>
      </DescriptionList>
      <Header>Advance your Career</Header>
      <DescriptionList>
        <li>
          <ButtonishAnchor
            href={
              currentUser?.premium
                ? routes.portfolio_preview_url({
                    from_navbar: true,
                    trigger: 'tools',
                  })
                : routes.portfolio_creation_url({
                    from_navbar: true,
                    trigger: 'tools',
                  })
            }
          >
            <Subheader>
              <div>Personal Website</div>
              <PremiumLock />
            </Subheader>
            <Description>Showcase your academic work</Description>
          </ButtonishAnchor>
        </li>
      </DescriptionList>
    </>
  );
};

export default ToolsMenu;
